/**
 * TemplatePreview — shows an email template rendered for a real customer (variables filled in).
 * Opened from the templates list (eye icon).
 *
 * Data (unchanged): searchCustomers({}) → first 50 customers that have an email (the first is
 * picked automatically) · previewEmailTemplate(templateId, customerId) → { subject, htmlBody,
 * customerData }.
 *
 * Props: templateId, templateName, onClose()
 */
import React, { useState, useEffect } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import * as actionsCreators from './../../../actions/index.js';
import { Modal, Button, Select, Spin, Field } from '../../../ui';
import classes from './TemplatePreview.css';

const TemplatePreview = (props) => {
  const [customers, setCustomers] = useState([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadCustomers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadCustomers = async () => {
    try {
      const results = await props.searchCustomers({ firstname: '', phone: '', mail: '' });
      // Only customers with an email address
      const customersWithEmail = results.filter((customer) => customer.mail && customer.mail.trim() !== '');
      setCustomers(customersWithEmail.slice(0, 50));
      if (customersWithEmail.length > 0) {
        setSelectedCustomerId(customersWithEmail[0].id);
        loadPreview(customersWithEmail[0].id);
      }
    } catch (error) {
      console.error('Error loading customers:', error);
    }
  };

  const loadPreview = async (customerId) => {
    if (!customerId) return;
    setLoading(true);
    try {
      const result = await props.previewEmailTemplate(props.templateId, customerId);
      setPreview(result);
    } catch (error) {
      console.error('Error loading preview:', error);
      props.showAlert('שגיאה בטעינת תצוגה מקדימה');
    }
    setLoading(false);
  };

  const handleCustomerChange = (customerId) => {
    setSelectedCustomerId(customerId);
    loadPreview(customerId);
  };

  return (
    <Modal
      open
      width={880}
      title={`תצוגה מקדימה · ${props.templateName}`}
      onCancel={() => props.onClose()}
      footer={<Button onClick={() => props.onClose()}>סגור</Button>}
    >
      <div className={classes.body}>
        <Field label="לקוח לדוגמה" hint="התבנית מוצגת עם הנתונים של הלקוח שנבחר">
          <Select
            showSearch
            optionFilterProp="label"
            value={selectedCustomerId || undefined}
            onChange={handleCustomerChange}
            options={customers.map((c) => ({
              value: c.id,
              label: `${c.firstname || ''} ${c.lastname || ''} (${c.mail || c.phone})`.trim(),
            }))}
          />
        </Field>

        {loading ? (
          <div className={classes.loading}>
            <Spin />
          </div>
        ) : preview ? (
          <>
            <div className={classes.mail}>
              <div className={classes.subject}>
                <span className={classes.subjectLabel}>נושא</span>
                {preview.subject}
              </div>
              {preview.htmlBody ? (
                <iframe srcDoc={preview.htmlBody} className={classes.iframe} title="Email Preview" sandbox="allow-same-origin" />
              ) : null}
            </div>

            {preview.customerData ? (
              <details className={classes.data}>
                <summary>נתוני הלקוח בתבנית</summary>
                <dl className={classes.dataList}>
                  {Object.entries(preview.customerData).map(([key, value]) => (
                    <React.Fragment key={key}>
                      <dt>{key}</dt>
                      <dd>{value || '(ריק)'}</dd>
                    </React.Fragment>
                  ))}
                </dl>
              </details>
            ) : null}
          </>
        ) : null}
      </div>
    </Modal>
  );
};

const mapStateToProps = () => ({});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      previewEmailTemplate: actionsCreators.previewEmailTemplate,
      searchCustomers: actionsCreators.searchCustomers,
      showAlert: actionsCreators.showAlert,
    },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(TemplatePreview);
